import { message } from "antd";
import i18next from "../../i18n";

export interface LocalProjectInfo {
    id: string // 项目ID
    type: "local" // 本地项目
    name: string
    path: string // 项目路径
    appId: string // 应用id
    updateTime: number // 更新时间
    category: "coding"|"model"|"ui"|"" // 程序设计/模型训练/界面设计
}

export interface CloudProjectInfo {
    id: string // appId+projectId
    type: "cloud" // 云端项目
    name: string
    projectId: string // 项目url
    appId: string // 应用id
    updateTime: number // 更新时间
    category: "coding"|"model"|"ui"|"" // 程序设计/模型训练/界面设计
    cover: string // 封面图片的完整url, 云端项目才有
}

export type ProjectInfo = LocalProjectInfo|CloudProjectInfo

class ProjectManager {
    onProjectsUpdate(callback: (data: ProjectInfo[]) => void) {
        // 纯React版本：设置刷新回调
        this.setRefreshCallback(callback);
        // 立即触发一次回调，返回当前项目列表
        callback(this._localProjects);
    }
    _localProjects: LocalProjectInfo[] = []
    refreshCallback?: ((data: ProjectInfo[]) => void)|null = null
    appsRefreshPromise: Promise<any> | null = null
    
    constructor() {
        // 纯React版本：从localStorage加载项目列表
        this.loadProjectsFromStorage();
    }

    // 从localStorage加载项目
    loadProjectsFromStorage() {
        try {
            const stored = localStorage.getItem('mindplus-projects');
            if (stored) {
                this._localProjects = JSON.parse(stored);
            }
        } catch (error) {
            console.log("加载项目列表失败", error);
            this._localProjects = [];
        }
    }

    // 保存项目到localStorage
    saveProjectsToStorage() {
        try {
            localStorage.setItem('mindplus-projects', JSON.stringify(this._localProjects));
        } catch (error) {
            console.log("保存项目列表失败", error);
        }
    }

    // 更新本地项目列表
    updateLocalProjects(projects: any[]) {
        this._localProjects = projects.map(p => ({
            id: p.inode || p.id,
            type: 'local' as const,
            name: p.name || 'Untitled Project',
            path: p.projectPath || p.path,
            appId: p.appId || 'unknown',
            updateTime: p.atime || p.updateTime || Date.now(),
            category: p.category || ''
        }));
        this.saveProjectsToStorage();
        this.refreshCallback?.(this._localProjects);
    }

    // 添加项目
    addProject(project: LocalProjectInfo) {
        const existingIndex = this._localProjects.findIndex(p => p.path === project.path);
        if (existingIndex >= 0) {
            this._localProjects[existingIndex] = project;
        } else {
            this._localProjects.push(project);
        }
        this.saveProjectsToStorage();
        this.refreshCallback?.(this._localProjects);
    }

    // 删除项目
    removeProject(projectPath: string) {
        this._localProjects = this._localProjects.filter(p => p.path !== projectPath);
        this.saveProjectsToStorage();
        this.refreshCallback?.(this._localProjects);
    }

    // 通过文件路径打开项目
    openProjectByPath(projectPath: string) {
        // 纯React版本：简单记录打开的项目
        const project = this._localProjects.find(p => p.path === projectPath);
        if (project) {
            localStorage.setItem('last-opened-project', projectPath);
            message.success(i18next.t('project_opened') as string);
        } else {
            message.error(i18next.t('project_not_found') as string);
        }
    }

    // 获取本地项目
    getLocalProjects() {
        return this._localProjects;
    }

    // 设置刷新回调
    setRefreshCallback(callback?: ((data: ProjectInfo[]) => void)|null) {
        this.refreshCallback = callback;
    }
}

export const projectManager = new ProjectManager();